import { createFeature, createReducer, on } from '@ngrx/store';

import * as AccountSettingsActions from './account-security.actions';

export const accountSettingsFeatureKey = 'accountSettings';

export interface AccountSecurityState {
  loading: boolean;
  error: string | null;
  qrCodeUrl: string | null;
  showQrModal: boolean;
  showOtpModal: boolean;
  showResetOtpModal: boolean;
  showSuccessModal: boolean;
  label: string | null;
  issuer: string | null;
  showCancelConfirmationModal: boolean;
  resetPasswordLoading: boolean;
  resetPasswordError: string | null;
  cancelOrigin: 'qr' | 'otp' | 'resetOtp' | null;
  initiateToken: string | null;
  initiateResetToken: string | null;
  verifiedToken: string | null;
  isResetFlow: boolean;
}

const INITIAL_STATE: AccountSecurityState = {
  loading: false,
  error: null,
  qrCodeUrl: null,
  showQrModal: false,
  showOtpModal: false,
  showResetOtpModal: false,
  showSuccessModal: false,
  label: null,
  issuer: null,
  showCancelConfirmationModal: false,
  cancelOrigin: null,
  resetPasswordLoading: false,
  resetPasswordError: null,
  initiateToken: null,
  initiateResetToken: null,
  verifiedToken: null,
  isResetFlow: false,
};

const reducer = createReducer(
  INITIAL_STATE,
  // ============================================
  // REGULAR GOOGLE AUTH SETUP FLOW
  // ============================================
  on(
    AccountSettingsActions.googleAuthStartSetup,
    (state: AccountSecurityState) => ({
      ...state,
      loading: true,
      error: null,
      showSuccessModal: false,
      showOtpModal: false,
      showQrModal: false,
      showResetOtpModal: false,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
      isResetFlow: false,
    })
  ),
  on(
    AccountSettingsActions.googleAuthStartSetupSuccess,
    (state: AccountSecurityState, { response }) => ({
      ...state,
      loading: false,
      qrCodeUrl: response?.qrCodeUrl ?? null,
      label: response?.label ?? null,
      issuer: response?.issuer ?? null,
      showQrModal: true,
      showOtpModal: false,
      showResetOtpModal: false,
      showSuccessModal: false,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
      isResetFlow: false,
    })
  ),
  on(
    AccountSettingsActions.googleAuthStartSetupFailure,
    (state: AccountSecurityState, { error }) => ({
      ...state,
      loading: false,
      error,
    })
  ),
  // ============================================
  // RESET GOOGLE AUTH FLOW
  // ============================================
  on(
    AccountSettingsActions.resetGoogleAuthInitiate,
    (state: AccountSecurityState) => ({
      ...state,
      loading: true,
      error: null,
      showSuccessModal: false,
      showOtpModal: false,
      showQrModal: false,
      showResetOtpModal: false,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
      initiateToken: null,
      verifiedToken: null,
      isResetFlow: true,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthInitiateSuccess,
    (state: AccountSecurityState, { response }) => ({
      ...state,
      loading: false,
      initiateToken: response.token,
      showResetOtpModal: true,
      showQrModal: false,
      showOtpModal: false,
      showSuccessModal: false,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
      isResetFlow: true,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthInitiateFailure,
    (state: AccountSecurityState, { error }) => ({
      ...state,
      loading: false,
      error,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthResendOtp,
    (state: AccountSecurityState) => ({
      ...state,
      loading: true,
      error: null,
      // Keep the modal open - don't change showResetOtpModal
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthResendOtpSuccess,
    (state: AccountSecurityState, { response }) => ({
      ...state,
      loading: false,
      initiateToken: response.token,
      // Keep the modal open - don't change showResetOtpModal
      error: null,
      isResetFlow: true,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthResendOtpFailure,
    (state: AccountSecurityState, { error }) => ({
      ...state,
      loading: false,
      error,
      // Keep the modal open - don't change showResetOtpModal
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthVerifyOtp,
    (state: AccountSecurityState) => ({
      ...state,
      loading: true,
      error: null,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthVerifyOtpSuccess,
    (state: AccountSecurityState, { token }) => ({
      ...state,
      loading: false,
      verifiedToken: token,
      showResetOtpModal: false,
      initiateResetToken: token,
      error: null,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthVerifyOtpFailure,
    (state: AccountSecurityState, { error }) => ({
      ...state,
      loading: false,
      error,
      showResetOtpModal: true,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthFetchQrCode,
    (state: AccountSecurityState) => ({
      ...state,
      loading: true,
      error: null,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthFetchQrCodeSuccess,
    (state: AccountSecurityState, { response }) => ({
      ...state,
      loading: false,
      qrCodeUrl: response?.qrCodeUrl ?? null,
      label: response?.label ?? null,
      issuer: response?.issuer ?? null,
      showQrModal: true,
      showOtpModal: false,
      showResetOtpModal: false,
      showSuccessModal: false,
      error: null,
    })
  ),
  on(
    AccountSettingsActions.resetGoogleAuthFetchQrCodeFailure,
    (state: AccountSecurityState, { error }) => ({
      ...state,
      loading: false,
      error,
    })
  ),
  // ============================================
  // COMMON ACTIONS (used by both flows)
  // ============================================
  on(
    AccountSettingsActions.googleAuthProceedToOtp,
    (state: AccountSecurityState) => ({
      ...state,
      showQrModal: false,
      showOtpModal: true,
      showResetOtpModal: false,
      showSuccessModal: false,
      error: null,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
    })
  ),
  on(
    AccountSettingsActions.googleAuthBackToQr,
    (state: AccountSecurityState) => ({
      ...state,
      showQrModal: true,
      showOtpModal: false,
      showResetOtpModal: false,
      showSuccessModal: false,
      error: null,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
    })
  ),
  on(
    AccountSettingsActions.googleAuthBackToOtp,
    (state: AccountSecurityState) => ({
      ...state,
      showQrModal: false,
      showOtpModal: false,
      showResetOtpModal: true,
      showSuccessModal: false,
      error: null,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
    })
  ),
  on(
    AccountSettingsActions.googleAuthVerifyOtp,
    (state: AccountSecurityState) => ({
      ...state,
      loading: true,
      error: null,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
    })
  ),
  on(
    AccountSettingsActions.googleAuthVerifyOtpSuccess,
    (state: AccountSecurityState) => ({
      ...state,
      loading: false,
      showOtpModal: false,
      showSuccessModal: true,
      showQrModal: false,
      showResetOtpModal: false,
      error: null,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
    })
  ),
  on(
    AccountSettingsActions.googleAuthVerifyOtpFailure,
    (state: AccountSecurityState, { error }) => ({
      ...state,
      loading: false,
      error,
      showOtpModal: true,
      showSuccessModal: false,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
    })
  ),
  on(
    AccountSettingsActions.googleAuthRequestCancelSetup,
    (state: AccountSecurityState, { origin }) => ({
      ...state,
      showCancelConfirmationModal: true,
      cancelOrigin: origin,
      showQrModal: false,
      showOtpModal: false,
      showResetOtpModal: false,
    })
  ),
  on(
    AccountSettingsActions.googleAuthDismissCancelSetup,
    (state: AccountSecurityState) => {
      if (state.cancelOrigin === 'otp') {
        return {
          ...state,
          showCancelConfirmationModal: false,
          cancelOrigin: null,
          showQrModal: false,
          showOtpModal: true,
          showResetOtpModal: false,
        };
      }
      if (state.cancelOrigin === 'qr') {
        return {
          ...state,
          showCancelConfirmationModal: false,
          cancelOrigin: null,
          showQrModal: true,
          showOtpModal: false,
          showResetOtpModal: false,
        };
      }
      if (state.cancelOrigin === 'resetOtp') {
        return {
          ...state,
          showCancelConfirmationModal: false,
          cancelOrigin: null,
          showQrModal: false,
          showOtpModal: false,
          showResetOtpModal: true,
        };
      }
      return {
        ...state,
        showCancelConfirmationModal: false,
        cancelOrigin: null,
      };
    }
  ),
  on(
    AccountSettingsActions.googleAuthCancelSetup,
    AccountSettingsActions.googleAuthCloseSuccess,
    () => ({
      ...INITIAL_STATE,
    })
  ),
  on(AccountSettingsActions.resetPassword, (state: AccountSecurityState) => ({
    ...state,
    resetPasswordLoading: true,
    resetPasswordError: null,
  })),
  on(
    AccountSettingsActions.resetPasswordSuccess,
    (state: AccountSecurityState) => ({
      ...state,
      resetPasswordLoading: false,
      resetPasswordError: null,
    })
  ),
  on(
    AccountSettingsActions.resetPasswordFailure,
    (state: AccountSecurityState, { error }) => ({
      ...state,
      resetPasswordLoading: false,
      resetPasswordError: error,
    })
  )
);

export const accountSecurityFeature = createFeature({
  name: 'accountSecurity',
  reducer,
});

export const {
  reducer: accountSecurityReducer,
  selectAccountSecurityState,
  selectLoading,
  selectError,
  selectQrCodeUrl,
  selectShowQrModal,
  selectShowOtpModal,
  selectShowResetOtpModal,
  selectShowSuccessModal,
  selectLabel,
  selectShowCancelConfirmationModal,
  selectIssuer,
  selectResetPasswordLoading,
  selectResetPasswordError,
  selectInitiateToken,
  selectInitiateResetToken,
  selectVerifiedToken,
  selectIsResetFlow,
} = accountSecurityFeature;
